/**
 * 文件处理工具函数
 * 包含：图片压缩、文件验证、预览生成等
 */

import { IMAGE_COMPRESS_CONFIG } from '../config/upload.config';

/**
 * 压缩图片
 * @param {File} file - 原始图片文件
 * @param {Object} options - 压缩选项
 * @returns {Promise<File>} 压缩后的文件
 */
export const compressImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = IMAGE_COMPRESS_CONFIG.maxWidth,
      maxHeight = IMAGE_COMPRESS_CONFIG.maxHeight,
      quality = IMAGE_COMPRESS_CONFIG.quality,
      mimeType = IMAGE_COMPRESS_CONFIG.mimeType
    } = options;

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // 计算新的尺寸
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        // 创建canvas进行压缩
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // 转换为blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // 创建新文件
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.\w+$/, '.jpg'), // 统一改为jpg
                { type: mimeType }
              );
              resolve(compressedFile);
            } else {
              reject(new Error('图片压缩失败'));
            }
          },
          mimeType,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * 批量压缩图片
 */
export const compressImages = async (files, options = {}) => {
  const compressedFiles = [];
  
  for (const file of files) {
    try {
      const compressed = await compressImage(file, options);
      compressedFiles.push(compressed);
    } catch (error) {
      console.error(`压缩图片 ${file.name} 失败:`, error);
      // 如果压缩失败，使用原文件
      compressedFiles.push(file);
    }
  }
  
  return compressedFiles;
};

/**
 * 生成图片预览URL
 */
export const getImagePreviewURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    
    reader.onerror = () => {
      reject(new Error('生成预览失败'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * 生成视频缩略图
 */
export const generateVideoThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.preload = 'metadata';
    video.muted = true;
    
    video.onloadedmetadata = () => {
      // 跳到第1秒截图
      video.currentTime = 1;
    };
    
    video.onseeked = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject(new Error('生成缩略图失败'));
        }
      }, 'image/jpeg', 0.7);
    };
    
    video.onerror = () => {
      reject(new Error('视频加载失败'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

/**
 * 验证文件类型
 */
export const validateFileType = (file, acceptedTypes) => {
  if (!file) return false;
  
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  
  return acceptedTypes.some(type => {
    if (type.startsWith('.')) {
      // 扩展名匹配
      return fileName.endsWith(type);
    } else if (type.includes('*')) {
      // MIME类型通配符匹配 (如 image/*)
      const regex = new RegExp(type.replace('*', '.*'));
      return regex.test(fileType);
    } else {
      // 精确MIME类型匹配
      return fileType === type;
    }
  });
};

/**
 * 验证文件大小
 */
export const validateFileSize = (file, maxSize) => {
  if (!file) return false;
  return file.size <= maxSize;
};

/**
 * 格式化文件大小
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  if (bytes === 1) return '1 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i];
};

/**
 * 获取文件扩展名
 */
export const getFileExtension = (fileName) => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return '';
  return fileName.substring(lastDot).toLowerCase();
};

/**
 * 从URL获取文件名
 */
export const getFileNameFromURL = (url) => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    return pathname.substring(pathname.lastIndexOf('/') + 1);
  } catch {
    return url.substring(url.lastIndexOf('/') + 1);
  }
};

/**
 * 检查是否为图片文件
 */
export const isImageFile = (file) => {
  if (!file) return false;
  return file.type.startsWith('image/');
};

/**
 * 检查是否为视频文件
 */
export const isVideoFile = (file) => {
  if (!file) return false;
  return file.type.startsWith('video/');
};

/**
 * 检查是否为音频文件
 */
export const isAudioFile = (file) => {
  if (!file) return false;
  return file.type.startsWith('audio/');
};

/**
 * 检查URL是否为图片
 */
export const isImageURL = (url) => {
  if (!url) return false;
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
  const lowerUrl = url.toLowerCase();
  return imageExts.some(ext => lowerUrl.includes(ext));
};

/**
 * 检查URL是否为视频
 */
export const isVideoURL = (url) => {
  if (!url) return false;
  const videoExts = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.webm'];
  const lowerUrl = url.toLowerCase();
  return videoExts.some(ext => lowerUrl.includes(ext));
};

/**
 * 检查URL是否为音频
 */
export const isAudioURL = (url) => {
  if (!url) return false;
  const audioExts = ['.mp3', '.wav', '.aac', '.flac', '.ogg', '.m4a'];
  const lowerUrl = url.toLowerCase();
  return audioExts.some(ext => lowerUrl.includes(ext));
};

/**
 * 创建下载链接
 */
export const downloadFile = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || getFileNameFromURL(url);
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * 读取文件为DataURL（用于预览）
 */
export const readFileAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
};

/**
 * 读取文件为Text
 */
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

/**
 * 读取文件为ArrayBuffer
 */
export const readFileAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
};

/**
 * 创建文件对象的简要信息
 */
export const getFileInfo = (file) => {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    sizeFormatted: formatFileSize(file.size),
    extension: getFileExtension(file.name),
    lastModified: file.lastModified,
    lastModifiedDate: new Date(file.lastModified)
  };
};

export default {
  compressImage,
  compressImages,
  getImagePreviewURL,
  generateVideoThumbnail,
  validateFileType,
  validateFileSize,
  formatFileSize,
  getFileExtension,
  getFileNameFromURL,
  isImageFile,
  isVideoFile,
  isAudioFile,
  isImageURL,
  isVideoURL,
  isAudioURL,
  downloadFile,
  readFileAsDataURL,
  readFileAsText,
  readFileAsArrayBuffer,
  getFileInfo
};

